import mfe from 'mime-file-extension';
import JSZip from 'jszip';
import * as _ from 'lodash-es';
import { getFileIdentifier } from 'cosmos-core';

export const readAsBinary = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // var arrayBuffer = reader.result,
      //   array = new Uint8Array(arrayBuffer),
      //   binaryString = String.fromCharCode.apply(null, array);

      // resolve(binaryString);

      const base64String = reader.result.replace(
        // eslint-disable-next-line no-useless-escape
        /^data:([\w]+?\/(([\w][\.|\-]?)+?));base64,/,
        ''
      );

      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const binarytoBlob = (data, contentType) => {
  const byteString = atob(data);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], {
    type: contentType,
  });
};

/**
 * It takes a zip file, unzips it, and returns an array of files
 * @param zipFile - The zip file that you want to unzip.
 * @returns An array of files.
 */
export const unzipFile = (zipFile) => {
  return JSZip.loadAsync(zipFile).then((archive) => {
    const files = Object.values(archive.files);

    const filesPromises = files
      .filter((f) => !f.dir)
      .map((f) =>
        f.async('blob').then((blob) => {
          try {
            const extension = getFileExtension(f.name);
            const type = mfe.getMimeType(extension);
            const filename = f.name.split('/').pop();
            const file = new File([blob], filename, {
              type,
            });
            const filePathParts = String(f.name).split('/');
            const filePath = _.chain(filePathParts)
              .dropRight()
              .join('/')
              .value();
            file.path = filePath;

            return Promise.resolve(file);
          } catch (err) {
            console.error(err);
          }
        })
      );

    return Promise.all(filesPromises);
  });
};

export const archiveContentTypes = [
  'application/zip',
  'application/x-zip-compressed',
];

export const flatMapArchives = async (files) => {
  let results = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    if (file != null) {
      if (archiveContentTypes.includes(file.type)) {
        try {
          const zipFiles = await unzipFile(file);
          const recursiveResults = await flatMapArchives(zipFiles);
          results = [...results, ...recursiveResults];
        } catch (err) {
          console.error(err);
          results = [...results, file];
        }
      } else {
        results = [...results, file];
      }
    }
  }

  return results;
};

export const getFileSize = (size) => {
  if (!size || isNaN(size)) return { size: 0, prettySize: '0 Bytes' };

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const parsedSize = parseInt(Math.floor(Math.log(size) / Math.log(1024)), 10);
  const prettySize = (size / 1024 ** parsedSize).toFixed(2);

  return {
    size: +prettySize,
    prettySize: `${prettySize} ${sizes[parsedSize]}`,
  };
};

export function getFilenameWithoutExtension(filename) {
  if(typeof filename !== 'string') {
    return ''; // Handle non-string inputs gracefully
  }

  if (filename.trim() === '') {
    return ''; // Handle empty or whitespace-only strings
  }

  // If the filename starts with a dot AND is not just a dot or multiple dots,
  // it's considered a "hidden file" where the leading dot is part of the name.
  // In this specific case, we don't remove any extension.
  // This rule applies only if the entire string isn't just dots.
  if (filename.startsWith('.') && filename.length > 1 && !/^\.+$/.test(filename)) {
    // We explicitly exclude cases like '.', '..', '...' from this rule
    // if the requirement is to keep them as is (which we'll handle next).
    // If it's a hidden file like '.config.json', we still *don't* remove the extension.
    return filename;
  }

  // Handle filenames that are just dots (e.g., '.', '..', '...')
  // and ensure no extension is removed.
  if (/^\.+$/.test(filename)) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf('.');

  // No dot found, or dot is the first character (already handled for hidden files not just dots),
  // or the dot is the last character (e.g., 'file.').
  // For 'file.' we will remove the trailing dot as per original logic, which seems reasonable.
  if (lastDotIndex === -1) {
    return filename; // No extension to remove
  }

  // If the last dot is the first character and it's not a multi-dot filename (e.g. '.gitignore' vs '...'),
  // this implies it's a hidden file with no further extension to strip, or a special directory like '.' or '..'.
  // This case should already be handled by the startsWith('.') check above
  // if you want to explicitly prevent removal for ALL leading-dot files.
  // Given your point 2, we want to keep leading-dot files as is.
  // So, if we reach here, it means we are dealing with a standard file extension.
  return filename.substring(0, lastDotIndex);
}

export const getFileExtension = (filename) => {
  if (typeof filename !== 'string') return '';
  return filename.split('.').pop();
};

export const createLinkToDownload = (objectUrl, fileName) => {
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.dispatchEvent(new MouseEvent('click'));
};

export const generateProperFilePath = (relativePath) => {
  const parts = String(relativePath).split('/');
  return _.chain(parts).drop().dropRight().join('/').value();
};

export const parseFilesToResources = (documentFiles) => {
  return _.chain(documentFiles)
    .map((f) => ({
      filename: f.name,
      displayname: getFilenameWithoutExtension(f.name),
      _path: f.path || '',
    }))
    .orderBy('_path')
    .value();
};

export const isContentTypeViewable = (contentType) => {
  return ![
    'application/x-zip-compressed',
    'application/zip',
    'audio/mpeg',
    'image/x-dwg',
  ].includes(contentType);
};

export const generateUniqueFileLabel = (filePath, fileName) => {
  if (!filePath || !filePath.length) {
    return fileName || '';
  }

  if (!fileName || !fileName.length) {
    return '';
  }

  if(filePath === fileName) {
    return fileName;
  }

  // if filePath exists, check if it ends with a slash
  const lastChar = filePath[filePath.length - 1];
  if (lastChar !== '/') {
    filePath += '/';
  }

  return filePath + fileName;
};

export const blobToFileInfo = (b, idx) => ({
  name: b.name,
  path: b.path || '',
  size: b.size,
  confidential: !!b.confidential,
  order: idx,
  placeholder: !!b.placeholder,
  type: b.type,
  fileIdentifier: getFileIdentifier(b),
});

export const extractFileName = (contentDisposition) => {
  if (!contentDisposition) return;

  const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (filenameStarMatch && filenameStarMatch[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch (err) {
      console.warn('Failed to decode filename*:', err);
      return filenameStarMatch[1];
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1];
  }

  const fallbackMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (fallbackMatch && fallbackMatch[1]) {
    return fallbackMatch[1].trim();
  }
}

export const unsupportedPdfConversionTypes = ['image/svg+xml', 'application/json'];
